import React, { useState, useEffect, useRef } from 'react';
import { hostelInventoryAPI } from '../../services/api';

const COMPLAINT_CATEGORIES = [
  { value: 'ELECTRICAL', label: '⚡ Electrical' },
  { value: 'PLUMBING', label: '🔧 Plumbing' },
  { value: 'FURNITURE', label: '🪑 Furniture Damage' },
  { value: 'CLEANING', label: '🧹 Cleaning' },
  { value: 'INTERNET', label: '📶 Internet/Wi-Fi' },
  { value: 'WATER', label: '💧 Water Supply' },
  { value: 'AC_COOLING', label: '❄️ AC/Cooling' },
  { value: 'SECURITY', label: '🔒 Security' },
  { value: 'REPAIRS', label: '🔨 Room Repairs' },
  { value: 'OTHER', label: '📋 Other' },
];

// Full 9-step status system + legacy
const STATUS_COLORS = {
  SUBMITTED: '#f97316',
  UNDER_VERIFICATION: '#3b82f6',
  CONFIRMED_DAMAGED: '#ef4444',
  UNDER_REPAIR: '#8b5cf6',
  AWAITING_REPLACEMENT: '#f59e0b',
  REPLACEMENT_ORDERED: '#0ea5e9',
  DELIVERED: '#06b6d4',
  INSTALLED: '#10b981',
  RESOLVED: '#22c55e',
  // Legacy
  OPEN: '#f97316',
  ASSIGNED: '#3b82f6',
  IN_PROGRESS: '#8b5cf6',
  CLOSED: '#6b7280',
};

const STATUS_ICONS = {
  SUBMITTED: '📋',
  UNDER_VERIFICATION: '🔍',
  CONFIRMED_DAMAGED: '⚠️',
  UNDER_REPAIR: '🔧',
  AWAITING_REPLACEMENT: '⏳',
  REPLACEMENT_ORDERED: '📦',
  DELIVERED: '🚚',
  INSTALLED: '✅',
  RESOLVED: '✅',
  OPEN: '📋',
  ASSIGNED: '👤',
  IN_PROGRESS: '🔧',
  CLOSED: '🔒',
};

const PRIORITY_COLORS = {
  HIGH: '#ef4444',
  MEDIUM: '#f59e0b',
  LOW: '#22c55e',
};

const ASSET_TYPE_LABELS = {
  BED: 'Bed', MATTRESS: 'Mattress', FAN: 'Fan', LIGHT: 'Light/Tube',
  TABLE: 'Study Table', CHAIR: 'Chair', CUPBOARD: 'Cupboard/Wardrobe',
  BUCKET: 'Bucket', MIRROR: 'Mirror', AC: 'Air Conditioner',
  GEYSER: 'Geyser', CURTAIN: 'Curtain', DUSTBIN: 'Dustbin',
  STUDY_LAMP: 'Study Lamp', SWITCHBOARD: 'Switchboard',
  WATER_BOTTLE: 'Water Jug', OTHER: 'Other',
};

export default function HostelUserPortalTab() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showComplaintForm, setShowComplaintForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const imageRef = useRef();

  const [form, setForm] = useState({
    title: '',
    category: '',
    description: '',
    priority: 'MEDIUM',
    image: null,
    asset: '',           // selected asset id or 'OTHER'
    asset_other_description: '',
  });

  useEffect(() => {
    hostelInventoryAPI.getMyHostelProfile()
      .then(data => { setProfile(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'image') setForm(f => ({ ...f, image: files[0] }));
    else setForm(f => ({ ...f, [name]: value }));
  };

  const handleSubmitComplaint = async (e) => {
    e.preventDefault();
    if (!form.category || !form.description.trim()) {
      setErrorMsg('Category and description are required.');
      return;
    }
    if (!form.asset) {
      setErrorMsg('Please select the affected accessory or choose "Other".');
      return;
    }
    if (form.asset === 'OTHER' && !form.asset_other_description.trim()) {
      setErrorMsg('Please describe the issue in the "Other" field.');
      return;
    }
    setSubmitting(true);
    setErrorMsg('');
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('category', form.category);
      fd.append('description', form.description);
      fd.append('priority', form.priority);
      if (form.image) fd.append('image', form.image);
      if (form.asset && form.asset !== 'OTHER') {
        fd.append('asset', form.asset);
      }
      if (form.asset === 'OTHER') {
        fd.append('asset_other_description', form.asset_other_description);
      }
      // room is auto-populated from allocation server-side
      await hostelInventoryAPI.submitComplaint(fd);
      setSuccessMsg('Complaint submitted successfully! The hostel admin will review it shortly.');
      setShowComplaintForm(false);
      setForm({ title: '', category: '', description: '', priority: 'MEDIUM', image: null, asset: '', asset_other_description: '' });
      if (imageRef.current) imageRef.current.value = '';
      // Refresh complaints list
      const updated = await hostelInventoryAPI.getMyHostelProfile();
      setProfile(updated);
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || 'Failed to submit complaint. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.center}>
        <div style={styles.spinner} />
        <p style={{ color: '#94a3b8', marginTop: 12 }}>Loading hostel info…</p>
      </div>
    );
  }

  if (!profile?.allocated) {
    return (
      <div style={styles.emptyState}>
        <div style={{ fontSize: 56 }}>🏠</div>
        <h3 style={styles.emptyTitle}>Not Currently Allocated</h3>
        <p style={styles.emptyText}>You don't have an active hostel allocation. Contact the hostel administration for more information.</p>
      </div>
    );
  }

  const { allocation, hostel, room, floor, building, bed_label, warden, roommates, my_complaints, room_assets } = profile;

  return (
    <div style={styles.container}>
      {successMsg && (
        <div style={styles.successBanner}>
          ✅ {successMsg}
          <button onClick={() => setSuccessMsg('')} style={styles.closeBanner}>×</button>
        </div>
      )}

      {/* ── Header Card ── */}
      <div style={styles.headerCard}>
        <div style={styles.headerLeft}>
          <div style={styles.hostelIcon}>🏨</div>
          <div>
            <h2 style={styles.hostelName}>{hostel?.name}</h2>
            <p style={styles.hostelMeta}>{hostel?.campus} · {hostel?.type}</p>
          </div>
        </div>
        <div style={styles.statusBadge}>
          <span style={{ color: '#22c55e', fontWeight: 700 }}>● ACTIVE</span>
        </div>
      </div>

      {/* ── Room Info Grid ── */}
      <div style={styles.grid3}>
        <InfoCard icon="🚪" label="Room Number" value={room?.room_number} accent="#6366f1" />
        <InfoCard icon="🛏️" label="Bed Assigned" value={bed_label || 'Not Assigned'} accent="#8b5cf6" />
        <InfoCard icon="🏠" label="Room Type" value={room?.room_type} accent="#06b6d4" />
        <InfoCard icon="🏢" label="Building / Block" value={building?.name + (building?.wing ? ` (${building.wing})` : '')} accent="#f59e0b" />
        <InfoCard icon="📐" label="Floor" value={floor?.floor_name || `Floor ${floor?.floor_number}`} accent="#10b981" />
        <InfoCard icon="📅" label="Admission Date" value={allocation?.allocation_date} accent="#ec4899" />
        <InfoCard icon="💰" label="Monthly Rent" value={room?.monthly_rent ? `₹${room.monthly_rent}` : 'N/A'} accent="#f97316" />
        <InfoCard icon="❄️" label="AC" value={room?.has_ac ? 'Available' : 'Not Available'} accent={room?.has_ac ? '#06b6d4' : '#6b7280'} />
        <InfoCard icon="📶" label="Wi-Fi" value={room?.has_wifi ? 'Available' : 'Not Available'} accent={room?.has_wifi ? '#22c55e' : '#6b7280'} />
      </div>

      {/* ── Warden Info ── */}
      {warden && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>👮 Warden / In-Charge</h3>
          <div style={styles.wardenCard}>
            <div style={styles.wardenAvatar}>{warden.name?.[0]?.toUpperCase() || 'W'}</div>
            <div>
              <div style={styles.wardenName}>{warden.name}</div>
              {warden.phone && <div style={styles.wardenContact}>📞 {warden.phone}</div>}
              {warden.email && <div style={styles.wardenContact}>✉️ {warden.email}</div>}
            </div>
          </div>
        </div>
      )}

      {/* ── Roommates ── */}
      {roommates?.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>👥 Roommates ({roommates.length})</h3>
          <div style={styles.roommatesGrid}>
            {roommates.map(r => (
              <div key={r.id} style={styles.roommateCard}>
                <div style={styles.roommateAvatar}>{(r.name || r.username)?.[0]?.toUpperCase()}</div>
                <div>
                  <div style={styles.roommateName}>{r.name}</div>
                  <div style={styles.roommateRole}>{r.role?.replace(/_/g, ' ')}</div>
                  {r.bed_label && <div style={styles.roommateRole}>Bed: {r.bed_label}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Room Accessories ── */}
      {room_assets?.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>🪑 Room Accessories</h3>
          <div style={styles.assetGrid}>
            {room_assets.map(a => (
              <div key={a.id} style={styles.assetChip}>
                <span style={styles.assetLabel}>{a.asset_label}</span>
                <span style={styles.assetType}>{ASSET_TYPE_LABELS[a.asset_type] || a.asset_type}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Complaint Section ── */}
      <div style={styles.section}>
        <div style={styles.sectionHeaderRow}>
          <h3 style={styles.sectionTitle}>📋 My Complaints</h3>
          <button style={styles.reportBtn} onClick={() => setShowComplaintForm(v => !v)}>
            {showComplaintForm ? '✕ Cancel' : '+ Report Issue'}
          </button>
        </div>

        {showComplaintForm && (
          <form onSubmit={handleSubmitComplaint} style={styles.complaintForm}>
            <h4 style={styles.formTitle}>Report a Hostel Complaint</h4>
            {errorMsg && <div style={styles.errorBanner}>{errorMsg}</div>}

            {/* Affected Accessory Selector */}
            <label style={styles.label}>Affected Accessory *</label>
            <select name="asset" value={form.asset} onChange={handleChange} style={styles.input} required>
              <option value="">— Select the affected item —</option>
              {(room_assets || []).map(a => (
                <option key={a.id} value={a.id}>
                  {a.asset_label} — {ASSET_TYPE_LABELS[a.asset_type] || a.asset_type}
                </option>
              ))}
              <option value="OTHER">📋 Other (not in the list above)</option>
            </select>

            {form.asset === 'OTHER' && (
              <>
                <label style={styles.label}>Describe the Issue (Other) *</label>
                <input
                  name="asset_other_description"
                  value={form.asset_other_description}
                  onChange={handleChange}
                  placeholder="e.g. Bathroom door hinge broken, Wall paint peeling…"
                  style={styles.input}
                  required
                />
              </>
            )}

            <label style={styles.label}>Complaint Title *</label>
            <input
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="e.g. Fan not working in room"
              style={styles.input}
              required
            />

            <div style={styles.row2}>
              <div style={{ flex: 1 }}>
                <label style={styles.label}>Category *</label>
                <select name="category" value={form.category} onChange={handleChange} style={styles.input} required>
                  <option value="">Select category…</option>
                  {COMPLAINT_CATEGORIES.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={styles.label}>Priority</label>
                <select name="priority" value={form.priority} onChange={handleChange} style={styles.input}>
                  <option value="LOW">🟢 Low</option>
                  <option value="MEDIUM">🟡 Medium</option>
                  <option value="HIGH">🔴 High</option>
                </select>
              </div>
            </div>

            <label style={styles.label}>Description *</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="Describe the issue in detail… when did it start, how severe, etc."
              style={{ ...styles.input, minHeight: 90, resize: 'vertical' }}
              required
            />

            <label style={styles.label}>Photo (optional)</label>
            <input type="file" name="image" accept="image/*" onChange={handleChange} ref={imageRef} style={styles.input} />

            <div style={styles.formActions}>
              <button type="button" onClick={() => setShowComplaintForm(false)} style={styles.cancelBtn}>Cancel</button>
              <button type="submit" disabled={submitting} style={styles.submitBtn}>
                {submitting ? 'Submitting…' : '📤 Submit Complaint'}
              </button>
            </div>
          </form>
        )}

        {/* Complaints List */}
        {my_complaints?.length > 0 ? (
          <div style={styles.complaintsList}>
            {my_complaints.map(c => (
              <div key={c.id} style={styles.complaintItem}>
                <div style={styles.complaintTop}>
                  <span style={styles.complaintTitle}>
                    {STATUS_ICONS[c.status] || '📋'} {c.title || c.category_display}
                  </span>
                  <span style={{ ...styles.badge, background: STATUS_COLORS[c.status] || '#6b7280' }}>
                    {c.status_display || c.status}
                  </span>
                </div>
                {/* Asset info */}
                {c.asset_label && (
                  <div style={styles.assetTag}>
                    🔖 Asset: <strong>{c.asset_label}</strong> ({c.asset_type_display || c.asset_type})
                    {c.asset_status && (
                      <span style={{ ...styles.miniStatus, background: c.asset_status === 'WORKING' ? '#22c55e' : c.asset_status === 'DAMAGED' ? '#ef4444' : '#f59e0b' }}>
                        {c.asset_status_display || c.asset_status}
                      </span>
                    )}
                  </div>
                )}
                {c.asset_other_description && (
                  <div style={styles.assetTag}>📋 Other: {c.asset_other_description}</div>
                )}
                <div style={styles.complaintMeta}>
                  <span style={{ ...styles.priorityDot, background: PRIORITY_COLORS[c.priority] }} />
                  {c.category_display} · {c.priority_display}
                </div>
                <p style={styles.complaintDesc}>{c.description}</p>
                {/* Repair info if applicable */}
                {c.repair_started_date && (
                  <div style={styles.repairInfo}>
                    🔧 Repair started: {c.repair_started_date}
                    {c.repair_completed_date && ` · Completed: ${c.repair_completed_date}`}
                    {c.repair_remarks && ` · ${c.repair_remarks}`}
                  </div>
                )}
                {c.resolution_notes && (
                  <div style={styles.resolutionNote}>✅ {c.resolution_notes}</div>
                )}
                <div style={styles.complaintDate}>
                  🕐 {new Date(c.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          !showComplaintForm && (
            <div style={styles.noComplaints}>No complaints filed yet.</div>
          )
        )}
      </div>
    </div>
  );
}

function InfoCard({ icon, label, value, accent }) {
  return (
    <div style={{ ...styles.infoCard, borderLeft: `3px solid ${accent}` }}>
      <div style={{ fontSize: 22, marginBottom: 4 }}>{icon}</div>
      <div style={styles.infoLabel}>{label}</div>
      <div style={styles.infoValue}>{value || '—'}</div>
    </div>
  );
}

const styles = {
  container: { padding: '16px 0', display: 'flex', flexDirection: 'column', gap: 20 },
  center: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 40 },
  spinner: {
    width: 36, height: 36, border: '3px solid #334155', borderTop: '3px solid #6366f1',
    borderRadius: '50%', animation: 'spin 0.8s linear infinite',
  },
  emptyState: {
    textAlign: 'center', padding: '60px 20px', color: '#94a3b8',
  },
  emptyTitle: { fontSize: 20, fontWeight: 700, color: '#cbd5e1', margin: '12px 0 8px' },
  emptyText: { fontSize: 14, maxWidth: 400, margin: '0 auto' },

  headerCard: {
    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
    border: '1px solid #334155',
    borderRadius: 12, padding: '20px 24px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 16 },
  hostelIcon: { fontSize: 36 },
  hostelName: { fontSize: 20, fontWeight: 700, color: '#f1f5f9', margin: 0 },
  hostelMeta: { fontSize: 13, color: '#94a3b8', margin: '2px 0 0' },
  statusBadge: {
    background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)',
    borderRadius: 20, padding: '4px 14px', fontSize: 13,
  },

  grid3: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
    gap: 12,
  },
  infoCard: {
    background: '#1e293b', border: '1px solid #334155',
    borderRadius: 10, padding: '14px 16px',
  },
  infoLabel: { fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 },
  infoValue: { fontSize: 15, fontWeight: 700, color: '#e2e8f0' },

  section: {
    background: '#1e293b', border: '1px solid #334155',
    borderRadius: 12, padding: '20px 24px',
  },
  sectionTitle: { fontSize: 16, fontWeight: 700, color: '#e2e8f0', margin: '0 0 16px' },
  sectionHeaderRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },

  assetGrid: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  assetChip: {
    background: '#0f172a', border: '1px solid #334155',
    borderRadius: 8, padding: '6px 12px',
    display: 'flex', flexDirection: 'column', gap: 2,
  },
  assetLabel: { fontSize: 13, fontWeight: 700, color: '#6366f1' },
  assetType: { fontSize: 11, color: '#64748b' },

  wardenCard: {
    display: 'flex', alignItems: 'center', gap: 14,
    background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)',
    borderRadius: 10, padding: '14px 18px',
  },
  wardenAvatar: {
    width: 44, height: 44, borderRadius: '50%',
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 18, fontWeight: 700, color: '#fff', flexShrink: 0,
  },
  wardenName: { fontSize: 15, fontWeight: 700, color: '#e2e8f0' },
  wardenContact: { fontSize: 13, color: '#94a3b8', marginTop: 2 },

  roommatesGrid: { display: 'flex', flexDirection: 'column', gap: 10 },
  roommateCard: {
    display: 'flex', alignItems: 'center', gap: 12,
    background: '#0f172a', borderRadius: 8, padding: '12px 14px',
    border: '1px solid #334155',
  },
  roommateAvatar: {
    width: 36, height: 36, borderRadius: '50%',
    background: 'linear-gradient(135deg, #0ea5e9, #06b6d4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 14, fontWeight: 700, color: '#fff', flexShrink: 0,
  },
  roommateName: { fontSize: 14, fontWeight: 600, color: '#e2e8f0' },
  roommateRole: { fontSize: 12, color: '#64748b', textTransform: 'capitalize' },

  reportBtn: {
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    border: 'none', borderRadius: 8, color: '#fff',
    padding: '8px 18px', cursor: 'pointer', fontWeight: 600, fontSize: 13,
  },

  complaintForm: {
    background: '#0f172a', border: '1px solid #334155',
    borderRadius: 10, padding: '20px', marginBottom: 16,
  },
  formTitle: { fontSize: 15, fontWeight: 700, color: '#e2e8f0', margin: '0 0 16px' },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, marginTop: 12 },
  input: {
    width: '100%', background: '#1e293b', border: '1px solid #334155',
    borderRadius: 8, padding: '10px 12px', color: '#e2e8f0', fontSize: 14,
    boxSizing: 'border-box', outline: 'none',
  },
  row2: { display: 'flex', gap: 12 },
  formActions: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 },
  cancelBtn: {
    background: 'transparent', border: '1px solid #334155', borderRadius: 8,
    color: '#94a3b8', padding: '8px 18px', cursor: 'pointer', fontSize: 13,
  },
  submitBtn: {
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    border: 'none', borderRadius: 8, color: '#fff',
    padding: '8px 22px', cursor: 'pointer', fontWeight: 700, fontSize: 14,
  },

  complaintsList: { display: 'flex', flexDirection: 'column', gap: 10 },
  complaintItem: {
    background: '#0f172a', border: '1px solid #334155',
    borderRadius: 10, padding: '14px 16px',
  },
  complaintTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 },
  complaintTitle: { fontSize: 14, fontWeight: 700, color: '#e2e8f0' },
  badge: {
    borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700,
    color: '#fff', textTransform: 'uppercase', whiteSpace: 'nowrap',
  },
  assetTag: {
    fontSize: 12, color: '#94a3b8', marginBottom: 4,
    display: 'flex', alignItems: 'center', gap: 6,
  },
  miniStatus: {
    borderRadius: 10, padding: '1px 8px', fontSize: 10, fontWeight: 700,
    color: '#fff', textTransform: 'uppercase',
  },
  complaintMeta: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#64748b', marginBottom: 4 },
  priorityDot: { width: 8, height: 8, borderRadius: '50%' },
  complaintDesc: { fontSize: 13, color: '#94a3b8', margin: '4px 0' },
  repairInfo: { fontSize: 12, color: '#8b5cf6', background: 'rgba(139,92,246,0.1)', borderRadius: 6, padding: '4px 10px', marginTop: 4 },
  resolutionNote: { fontSize: 12, color: '#22c55e', background: 'rgba(34,197,94,0.08)', borderRadius: 6, padding: '4px 10px', marginTop: 4 },
  complaintDate: { fontSize: 11, color: '#475569', marginTop: 4 },
  noComplaints: { fontSize: 13, color: '#64748b', textAlign: 'center', padding: '16px 0' },

  successBanner: {
    background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)',
    borderRadius: 8, padding: '10px 16px', color: '#22c55e', fontSize: 14,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  errorBanner: {
    background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: 8, padding: '10px 14px', color: '#ef4444', fontSize: 13, marginBottom: 8,
  },
  closeBanner: { background: 'none', border: 'none', color: '#22c55e', cursor: 'pointer', fontSize: 18, lineHeight: 1 },
};
